import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Layout from '../components/Layout'
import DescriptionList from '../components/shared/DescriptionList'
import AircraftOptions from '../components/aircraft/AircraftOptions'
import AircraftNotes from '../components/notes/AircraftNotes'
import AircraftCompanies from '../components/aircraft/AircraftCompanies'
import RelativeValueBreakdown from '../components/aircraft/RelativeValueBreakdown'
import OverrideDataModal from '../components/overrides/OverrideDataModal'
import AircraftChangeIndicator from '../components/aircraft/AircraftChangeIndicator'
import AircraftMovementControls from '../components/aircraft/AircraftMovementControls'
import MovementHistoryPanel from '../components/aircraft/MovementHistoryPanel'
import { AircraftService } from '../services/intellijetnet/aircraft.service'
import { ModelInfoCacheService } from '../services/intellijetnet/model-info-cache.service'
import { OverridesService } from '../services/overrides.service'
import { OffMarketService } from '../services/off-market.service'
import { UnifiedAircraftService } from '../services/unified-aircraft.service'
import { AircraftTransactionsService } from '../services/aircraft-transactions.service'
import { Aircraft } from '../types/intellijetnet'
import { AircraftOverride, OffMarket, MarketCategory, AircraftChangeInfo, AircraftTransaction } from '../types/database'
import { capitalizeMakeName, formatModelName, formatPrice, formatDate } from '../utils/formatters'
import { PriceWithModifier } from '../components/shared/PriceWithModifier'
import { getEffectiveAircraftData } from '../utils/overrides'
import { detectAircraftContainer } from '../utils/container-detection'
import { detectAircraftChanges } from '../utils/change-detection'
import { useOverrideRefresh } from '../contexts/OverrideRefreshContext'

export default function AircraftDetail() {
  const { modelId, aircraftId } = useParams<{ modelId: string; aircraftId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [aircraft, setAircraft] = useState<Aircraft | null>(null)
  const [modelInfo, setModelInfo] = useState<{
    model_id: number
    make_name: string
    model_name: string
    display_name: string
  } | null>(null)
  const [override, setOverride] = useState<AircraftOverride | null>(null)
  const [offMarket, setOffMarket] = useState<OffMarket | null>(null)
  const [salesHistory, setSalesHistory] = useState<AircraftTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showOverrideModal, setShowOverrideModal] = useState(false)
  const [changeInfo, setChangeInfo] = useState<AircraftChangeInfo | null>(null)
  const { refreshTrigger, lastRefreshedAircraft, triggerOverrideRefresh } = useOverrideRefresh()

  // Detect current container for movement controls
  const currentContainer = aircraft ? detectAircraftContainer(aircraft, [], [], location.pathname) : 'for_sale'

  // Check if this is an off-market aircraft based on the URL
  const isOffMarket = location.pathname.includes('/off-market/')

  useEffect(() => {
    if (modelId && aircraftId) {
      fetchData()
    }
    // Always scroll to top when component mounts
    window.scrollTo(0, 0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, aircraftId])

  // Refresh override data when refresh is triggered for this aircraft
  useEffect(() => {
    const numericAircraftId = parseInt(aircraftId!, 10)
    if (!isNaN(numericAircraftId) && lastRefreshedAircraft === numericAircraftId) {
      const refreshOverrideData = async () => {
        try {
          const overrideData = await OverridesService.getByAircraftId(numericAircraftId)
          setOverride(overrideData)
        } catch (err) {
          // Error refreshing override data
        }
      }
      refreshOverrideData()
    }
  }, [refreshTrigger, lastRefreshedAircraft, aircraftId])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const numericModelId = parseInt(modelId!, 10)
      const numericAircraftId = parseInt(aircraftId!, 10)

      if (isNaN(numericModelId) || isNaN(numericAircraftId)) {
        throw new Error('Invalid model or aircraft ID')
      }

      // Fetch model info
      const info = await ModelInfoCacheService.getModelInfo(numericModelId)
      if (!info) {
        throw new Error('Model not found')
      }
      setModelInfo(info)

      // Fetch aircraft details
      const aircraftData = await AircraftService.getById(numericAircraftId)
      if (!aircraftData) {
        throw new Error('Aircraft not found')
      }
      setAircraft(aircraftData)

      // Fetch override data
      const overrideData = await OverridesService.getByAircraftId(numericAircraftId)
      setOverride(overrideData)

      // If this is an off-market aircraft, fetch off-market data
      if (isOffMarket) {
        const offMarketData = await OffMarketService.getByModelId(numericModelId)
        const currentOffMarket = offMarketData.find(om => om.aircraft_id === numericAircraftId)
        setOffMarket(currentOffMarket || null)
      }

      // Fetch sales history for this aircraft
      try {
        const transactions = await AircraftTransactionsService.getByAircraftId(numericAircraftId)
        setSalesHistory(transactions)
      } catch (transactionErr) {
        setSalesHistory([])
      }

      // Detect changes for API aircraft (not off-market, skip for sold container)
      if (!isOffMarket && aircraftData && currentContainer !== 'sold') {
        try {
          const offMarketList = isOffMarket ? [{ aircraft_id: numericAircraftId, model_id: numericModelId, market_category: 'for_sale' as MarketCategory, id: '', created_at: '', updated_at: '' }] : []
          const aircraftChangeInfo = await detectAircraftChanges(aircraftData, overrideData, offMarketList)
          setChangeInfo(aircraftChangeInfo)
        } catch (changeErr) {
          // Don't fail the whole page if change detection fails
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const formatDamageHistory = (value: string | null): string => {
    if (!value) return '-'
    switch (value) {
      // API format
      case 'Y': return 'Yes'
      case 'N': return 'No'
      case 'U': return 'Unknown'
      // Database format (from overrides)
      case 'Yes': return 'Yes'
      case 'No': return 'No'
      case 'Unknown': return 'Unknown'
      default: return value
    }
  }

  const formatBoolean = (value: boolean | null): string => {
    if (value === null) return '-'
    return value ? 'Yes' : 'No'
  }

  const handleOverrideSave = (savedOverride: AircraftOverride | null) => {
    setOverride(savedOverride)
  }

  const handleChangeAcknowledged = () => {
    // Refresh change info after acknowledgment (skip for sold container)
    if (aircraft && !isOffMarket && currentContainer !== 'sold') {
      const refreshChangeInfo = async () => {
        try {
          const offMarketList: OffMarket[] = []
          const aircraftChangeInfo = await detectAircraftChanges(aircraft, override, offMarketList)
          setChangeInfo(aircraftChangeInfo)
        } catch (error) {
          // Error refreshing change info
        }
      }
      refreshChangeInfo()
    }
    // Trigger a refresh so MarketTable re-runs change detection when user navigates back
    triggerOverrideRefresh()
  }

  const handleCategoryChange = async (newCategory: MarketCategory) => {
    if (!offMarket) return

    try {
      await OffMarketService.updateCategory(offMarket.id, newCategory)
      setOffMarket({ ...offMarket, market_category: newCategory })
      // Navigate to the appropriate market section
      navigate(`/market/${modelId}`)
    } catch (err: any) {
      alert('Failed to update category: ' + err.message)
    }
  }

  const handleRemoveFromMarket = async () => {
    if (!aircraft) return

    if (window.confirm('Are you sure you want to remove this aircraft from the market summary? This action cannot be undone.')) {
      try {
        await UnifiedAircraftService.removeFromOffMarket(aircraft.aircraft_id)
        // Navigate back to market summary
        navigate(`/market/${modelId}`)
      } catch (err: any) {
        alert('Failed to remove aircraft: ' + err.message)
      }
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg text-gray-600">Loading aircraft information...</div>
        </div>
      </Layout>
    )
  }

  if (error || !aircraft || !modelInfo) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg text-red-600 mb-4">{error || 'Aircraft not found'}</div>
            <button
              onClick={() => navigate(`/market/${modelId}`)}
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              ← Back to Market
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  // Get effective aircraft data with overrides applied
  const effectiveAircraft = getEffectiveAircraftData(aircraft, override)

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="w-full px-2 sm:px-4 lg:px-6 xl:px-8 py-6">
        <div className="w-full max-w-full">
          <div className="mb-6">
            <button
              onClick={() => navigate(`/market/${modelId}`)}
              className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center"
            >
              ← Back to Market
            </button>
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <h1 className="text-3xl font-bold text-purple-950">
                  {capitalizeMakeName(modelInfo.make_name)} {formatModelName(modelInfo.model_name)} - {aircraft.sernbr}
                </h1>
                {isOffMarket && offMarket && (
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700">Category:</span>
                      <select
                        value={offMarket.market_category}
                        onChange={(e) => handleCategoryChange(e.target.value as MarketCategory)}
                        className="text-sm border border-gray-300 rounded-md px-3 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="for_sale">For Sale</option>
                        <option value="recently_sold">Recently Sold</option>
                        <option value="removed_from_market">Removed From Market</option>
                      </select>
                    </div>
                    <button
                      onClick={handleRemoveFromMarket}
                      className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded-md transition-colors duration-150"
                      title="Remove from market summary"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Remove from Summary</span>
                    </button>
                  </div>
                )}


                {/* Aircraft Movement Controls */}
                {modelId && (
                  <AircraftMovementControls
                    aircraft={aircraft}
                    modelId={parseInt(modelId, 10)}
                    currentContainer={currentContainer}
                    onUpdate={() => {
                      // Refresh the page to show the updated container status
                      window.location.reload()
                    }}
                    className="ml-4"
                  />
                )}
              </div>
              <button
                onClick={() => setShowOverrideModal(true)}
                className="bg-purple-950 text-white hover:bg-purple-900 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150"
              >
                Override Data
              </button>
            </div>
          </div>

          {/* Market Change Indicator - Show only for API aircraft with changes */}
          {!isOffMarket && changeInfo && (
            <div className="mb-8">
              <AircraftChangeIndicator
                changeInfo={changeInfo}
                onAcknowledged={handleChangeAcknowledged}
              />
            </div>
          )}

          {/* Full-width information sections */}
          <div className="space-y-8">
            {/* Notes */}
            <AircraftNotes
              aircraftId={aircraft.aircraft_id}
              modelId={aircraft.model_id || parseInt(modelId!, 10)}
            />

            {/* Aircraft Details */}
            <div className="bg-white shadow-sm rounded-lg p-6">
              <DescriptionList columns={2} sections={[
                {
                  title: 'Basic Information',
                  subtitle: 'Aircraft identification and registration details',
                  items: [
                    { label: 'Serial Number', value: aircraft.sernbr },
                    { label: 'Registration Number', value: aircraft.regnbr },
                    { label: 'Aircraft ID', value: aircraft.aircraft_id },
                  ]
                },
                {
                  title: 'Manufacturing',
                  subtitle: 'Production and manufacturing details',
                  items: [
                    { label: 'Year Manufactured', value: effectiveAircraft.yearmfr },
                    { label: 'Year Delivered', value: effectiveAircraft.yeardlv },
                    { label: 'Make', value: aircraft.make },
                    { label: 'Model', value: aircraft.model },
                    { label: 'Make Type', value: aircraft.maketype },
                  ]
                },
                {
                  title: 'Technical Specifications',
                  subtitle: 'Aircraft technical configuration',
                  items: [
                    { label: 'Airframe Type', value: aircraft.airframetype },
                    { label: 'Engine Model', value: aircraft.engmodel },
                    { label: 'Lifecycle', value: aircraft.lifecycle },
                    { label: '# PAX', value: effectiveAircraft.pax },
                  ]
                },
                {
                  title: 'Usage',
                  subtitle: 'Flight hours and operational history',
                  items: [
                    { label: 'AFTT (Airframe Total Time)', value: effectiveAircraft.aftt, formatter: (v) => v !== null ? v.toLocaleString() : '-' },
                    { label: 'TBO (Time Before Overhaul)', value: effectiveAircraft.tbo, formatter: (v) => v !== null ? v.toLocaleString() : '-' },
                    { label: 'Landings', value: effectiveAircraft.landings, formatter: (v) => v !== null ? v.toLocaleString() : '-' },
                    { label: 'Usage Type', value: aircraft.usage },
                  ]
                },
                {
                  title: 'Location',
                  subtitle: 'Current base location',
                  items: [
                    { label: 'Base IATA', value: aircraft.baseiata },
                    { label: 'Base Airport', value: effectiveAircraft.baseairport },
                    { label: 'Base City', value: effectiveAircraft.basecity },
                    { label: 'Base State', value: effectiveAircraft.basestate },
                    { label: 'Base Country', value: effectiveAircraft.basecountry },
                  ]
                },
                {
                  title: 'Market Status',
                  subtitle: 'Current market and pricing information',
                  items: [
                    { label: 'For Sale', value: effectiveAircraft.forsale, formatter: formatBoolean },
                    { label: 'Leased', value: effectiveAircraft.leased, formatter: formatBoolean },
                    { label: 'JN Ask Price', value: aircraft.asking, formatter: formatPrice },
                    { label: 'Actual Ask Price', value: override?.actual_ask_make_offer ? 'M.O.' : formatPrice(effectiveAircraft.asking) },
                    { label: 'Adj. Ask Price', value: override?.adj_ask_make_offer ? 'M.O.' : formatPrice(override?.external_asking_price?.toString() ?? effectiveAircraft.asking) },
                    { label: 'Actual Take Price', value: <PriceWithModifier value={effectiveAircraft.take_price} modifier={override?.take_price_modifier} /> },
                    { label: 'Adj. Take Price', value: <PriceWithModifier value={override?.external_take_price ?? effectiveAircraft.take_price} modifier={override?.external_take_price_modifier} /> },
                    { label: 'Status', value: effectiveAircraft.status },
                  ]
                },
                {
                  title: 'Maintenance',
                  subtitle: 'Maintenance program and history',
                  items: [
                    { label: 'Maintenance Program', value: effectiveAircraft.mxprog },
                    { label: 'Maintained', value: aircraft.maintained },
                    { label: 'Damage History', value: effectiveAircraft.damagehistoryyn, formatter: formatDamageHistory },
                  ]
                },
                {
                  title: 'Important Dates',
                  subtitle: 'Key dates in aircraft history',
                  items: [
                    {
                      label: 'Date Listed',
                      value: effectiveAircraft.status === 'Off Market' ? 'Off Market' : effectiveAircraft.datelisted,
                      formatter: effectiveAircraft.status === 'Off Market' ? (v) => v : formatDate
                    },
                    { label: 'Date Purchased', value: effectiveAircraft.datepurchased, formatter: formatDate },
                    { label: 'Times Current Date', value: aircraft.timescurrentdate, formatter: formatDate },
                  ]
                },
                {
                  title: 'System Information',
                  subtitle: 'Database and sync information',
                  items: [
                    { label: 'Active', value: aircraft.is_active, formatter: formatBoolean },
                    { label: 'Last Modified', value: aircraft.jetnet_last_modified, formatter: formatDate },
                    { label: 'Created At', value: aircraft.created_at, formatter: formatDate },
                    { label: 'Updated At', value: aircraft.updated_at, formatter: formatDate },
                  ]
                },
              ]} />
            </div>

            {/* Relative Value Breakdown */}
            <RelativeValueBreakdown
              aircraft={aircraft}
              override={override}
              modelId={aircraft.model_id || parseInt(modelId!, 10)}
            />

            {/* Sales History */}
            {salesHistory.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Sales History ({salesHistory.length} {salesHistory.length === 1 ? 'Sale' : 'Sales'})
                </h3>
                <div className="space-y-4">
                  {salesHistory.map((transaction, index) => (
                    <div key={transaction.transaction_id} className="border-l-4 border-blue-500 pl-4 py-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            Sale #{salesHistory.length - index}
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatDate(transaction.transaction_date)}
                          </div>
                        </div>
                        <div className="text-right">
                          {transaction.sale_price && (
                            <div className="text-sm font-medium text-gray-900">
                              {formatPrice(transaction.sale_price.toString())}
                            </div>
                          )}
                          {transaction.asking_price && (
                            <div className="text-xs text-gray-500">
                              Ask: {formatPrice(transaction.asking_price.toString())}
                            </div>
                          )}
                        </div>
                      </div>
                      {transaction.transaction_notes && (
                        <div className="mt-2 text-sm text-gray-600">
                          {transaction.transaction_notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Editable sections at the bottom */}
          <div className="mt-8 space-y-8">
            {/* Optional Equipment Configuration */}
            <AircraftOptions
              aircraftId={aircraft.aircraft_id}
              modelId={aircraft.model_id || parseInt(modelId!, 10)}
              readonly={false}
            />

            {/* Associated Companies */}
            <AircraftCompanies
              aircraftId={aircraft.aircraft_id}
              override={override}
            />

            {/* Movement History */}
            {modelId && (
              <MovementHistoryPanel
                aircraft={aircraft}
                modelId={parseInt(modelId, 10)}
                onUpdate={() => {
                  // Refresh the page to show updated movement status
                  window.location.reload()
                }}
              />
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Override Data Modal */}
      <OverrideDataModal
        isOpen={showOverrideModal}
        onClose={() => setShowOverrideModal(false)}
        aircraft={aircraft}
        onSave={handleOverrideSave}
      />
    </Layout>
  )
}